"use client"

import type React from "react"

import { useRef, useState } from "react"
import { Loader2, AlertTriangle, Mail, Bell, Save, CalendarCheck, Check } from "lucide-react"

import { analytics, stashEmailCaptureAttribution, trackEvent } from "@/lib/analytics"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import type { EmailSuggestion } from "@/lib/email-typo-detection"
import type { ReferencePhoto } from "@/data/grass-reference-photos"
import type { SavedPlanAnalyticsContext } from "@/lib/saved-plan-analytics"

export interface ResultUpgradeCardCopy {
  emailCtaLabel?: string
  emailPlaceholder?: string
  emailAriaLabel?: string
  emailDividerLabel?: string
  emailRevealLabel?: string
  googleLabel?: string
  appleLabel?: string
}

interface ResultUpgradeCardProps {
  email: string
  emailSuggestion: EmailSuggestion | null
  isSubmitting: boolean
  timelineRedirectUrl?: string
  savedPlanContext?: SavedPlanAnalyticsContext
  /** Experiment `diagnosis_cta_copy_v1`: `specific` names the diagnosed problem. */
  ctaVariant?: "control" | "specific"
  /** Primary diagnosed problem name, used by the `specific` arm. */
  problemName?: string
  /** Identified grass display name — personalizes the ask ("Save your Bermudagrass care plan"). */
  grassName?: string
  /** Reference photo record for the identified grass; renders a thumbnail (credit as its title tooltip). */
  grassPhoto?: ReferencePhoto | null
  /** True when the ID is high-confidence or user-picked; gates species-claiming copy. */
  grassNameIsConfirmed?: boolean
  /** Fired when the user takes the card's conversion action (OAuth click or email submit). */
  onPrimaryCtaClick?: (method: "oauth" | "email") => void
  /** Source for email-input focus analytics. Defaults to the original identify result card source. */
  source?: string
  /** `placement` for the OAuth buttons. Defaults to `${source}_upgrade_card`;
   *  hosts that share a source (digest and save modal) pass their own. */
  placement?: string
  /** Ref onto the email field so an owning surface can send the caret here from
   *  another CTA (the diagnose hero CTA and the actions strip both do). */
  emailInputRef?: React.Ref<HTMLInputElement>
  /** Fires on every focus of the email field, unlike the once-per-mount
   *  `result_email_capture_focused` analytics. Owners use it to retire
   *  competing asks once the user is engaged with this one. */
  onEmailFocus?: () => void
  /** Inline validation error supplied by the owning hook. */
  emailError?: string | null
  /** Runs immediately before an OAuth redirect. */
  onBeforeOAuth?: () => void | Promise<void>
  /** Outcome-named line above the OAuth buttons, e.g. "Your Bermudagrass plan for 53703 is ready — save it in one tap." */
  contextLine?: string
  /** Rollout `result_email_primary_v1`: `email_first` promotes the single-field
      email ask to the primary CTA and demotes OAuth account creation to a quiet
      secondary reveal. `account_first` (default) is today's behavior. NOTE: must
      not run concurrently with `result_email_demotion_v1` (contradictory). */
  askOrder?: "account_first" | "email_first"
  /** When true, frames the email ask as unlocking the visible 12-month plan. */
  unlockFraming?: boolean
  /** 1a account-first treatment (Post Analysis Page design, 2026-07-06):
      stacked pill OAuth buttons as THE action, email demoted to a quiet
      "or get it by email" reveal link.
      Embedded account_first only. */
  savePlanOauth?: boolean
  /** Opens the email lane under `savePlanOauth` without the user clicking the
      "or get it by email" reveal link. Controlled by the owning surface, which
      flips it when one of its OWN CTAs means "email it to me" — the diagnose
      hero CTA and actions strip both do, and they land the caret via
      `emailInputRef`, which is null while the field is unmounted. A constant
      `true` keeps the lane permanently on screen. OAuth stays visually primary
      either way (it still leads, above the divider). Embedded only. */
  emailRevealed?: boolean
  /** Fires when the card's OWN reveal link opens the email lane, so a surface
      driving `emailRevealed` stays in sync and doesn't re-count the open. */
  onEmailRevealed?: () => void
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
  onAcceptSuggestion: () => void
  onDismissSuggestion: () => void
  className?: string
  /**
   * Render without the outer card chrome (border, padding, background, shadow,
   * `#care-plan-upgrade` id). Used inside `ResultCarePlanDigest`, which owns the
   * shared card and the scroll-target id.
   */
  embedded?: boolean
  /** Keeps the proven account-first form structure while allowing the saved
   * result noun to match the current analysis mode. Defaults preserve the
   * existing Identify and Diagnose copy exactly. */
  copy?: ResultUpgradeCardCopy
}

export function ResultUpgradeCard({
  email,
  emailSuggestion,
  isSubmitting,
  timelineRedirectUrl,
  savedPlanContext,
  ctaVariant = "control",
  problemName,
  grassName,
  grassPhoto,
  grassNameIsConfirmed = false,
  onPrimaryCtaClick,
  source = "result_upgrade_card",
  placement,
  emailInputRef,
  onEmailFocus,
  emailError,
  onBeforeOAuth,
  contextLine,
  askOrder = "account_first",
  unlockFraming = false,
  savePlanOauth = false,
  emailRevealed = false,
  onEmailRevealed,
  onEmailChange,
  onEmailKeyPress,
  onSubmit,
  onAcceptSuggestion,
  onDismissSuggestion,
  className,
  embedded = false,
  copy,
}: ResultUpgradeCardProps) {
  const trimmedProblem = problemName?.trim() || ""
  const useSpecific = ctaVariant === "specific" && trimmedProblem.length > 0
  // Grass personalization only claims a species when the ID is confirmed
  // (high-confidence or user-picked) — a named heading on a best-guess result
  // would overclaim.
  const trimmedGrass = grassName?.trim() || ""
  const useGrass = grassNameIsConfirmed && trimmedGrass.length > 0
  const heading = useSpecific
    ? `Your full ${trimmedProblem} treatment plan`
    : useGrass
      ? `Save your ${trimmedGrass} care plan`
      : "See your full 12-month plan"
  const subheading = unlockFraming
    ? "Unlock your full 12-month plan. Free, no card required."
    : useGrass
      ? `Your 12-month ${trimmedGrass} schedule, timed to your ZIP. Free, no card required.`
      : "Free, no card required."
  // Both named paths keep this button short and STATIC, because it sits in a
  // ~270px-wide result column that fits ~23 characters on one line.
  //
  // The grass path learned this first: embedding "Mixed Cool-Season Blend" only
  // forced a two-line label. The problem path then repeated the mistake with a
  // 28-char cap, which let real diagnoses through untouched — "Localized turf
  // dieback" (22) produced "Email my Localized turf dieback plan" (36) and wrapped
  // in production. A tighter character budget is not the fix either: it either
  // truncates into awkward fragments ("Email my Summer drought plan") or has to be
  // set so low (~9 chars) that it rejects "Brown Patch", the most common diagnosis
  // we have. Any name-embedding rule here is a wrap bug waiting for a longer name.
  //
  // Specificity stays where it has room: the full diagnosis leads the heading
  // directly above ("Your full <problem> treatment plan"), and the divider above
  // that reads "OR EMAIL ME THE PLAN". The button only has to name the artifact
  // and say which of the two lanes it is — hence "Email my …" over "Send my …",
  // since the OAuth pills sit directly above it. Callers needing a bespoke label
  // still have `copy.emailCtaLabel`.
  const defaultSendLabel = useSpecific
    ? "Email my treatment plan"
    : useGrass
      ? "Email me my plan"
      : "Send my care plan"
  const sendLabel = copy?.emailCtaLabel ?? defaultSendLabel
  const photo = useGrass ? grassPhoto : null

  const [emailExpanded, setEmailExpanded] = useState(false)
  const [oauthRevealed, setOauthRevealed] = useState(false)
  const focusTrackedRef = useRef(false)
  const emailFirst = askOrder === "email_first"

  const handleRevealEmail = () => {
    setEmailExpanded(true)
    onEmailRevealed?.()
    analytics.softEmailCaptureOpened(source, {
      grassType: trimmedGrass || undefined,
    })
  }

  const handleEmailFocus = () => {
    onEmailFocus?.()
    if (focusTrackedRef.current) return
    focusTrackedRef.current = true
    const startProps = {
      source,
      analysis_id: savedPlanContext?.analysisId,
      funnel_id: savedPlanContext?.funnelId,
    }
    // email_capture_started is the site-wide start event; the legacy name keeps
    // firing with the same properties for continuity.
    analytics.emailCaptureStarted(startProps)
    trackEvent('result_email_capture_focused', startProps)
  }

  const oauthPlacement = placement ?? `${source}_upgrade_card`

  const handleOAuthBeforeRedirect = async () => {
    onPrimaryCtaClick?.("oauth")
    // Stash first so every caller gets a `capture_source` on signup_completed;
    // a caller that stashes its own (more specific) source in onBeforeOAuth
    // overwrites this one, which is the intended precedence.
    stashEmailCaptureAttribution(source)
    await onBeforeOAuth?.()
  }

  return (
    <div
      id={embedded ? undefined : "care-plan-upgrade"}
      className={cn(
        "relative w-full max-w-full min-w-0 overflow-hidden",
        embedded
          ? ""
          : "rounded-2xl border border-gray-200 bg-white p-5 md:p-8 scroll-mt-24 shadow-sm",
        className,
      )}
    >
      {/* Digest (embedded) mode: the surrounding card already established the
          plan value (this-month tasks + locked months) and owns the lead-in
          line, so the big heading, photo, and benefit-chip grid would just
          restate it — render nothing above the ask. */}
      {!embedded && (
        <>
          <div className="mb-4 text-center">
            <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.06em] text-green-700 mb-1.5">
              Unlock more
            </div>
            {photo && (
              <img
                src={photo.src}
                alt={`${trimmedGrass} reference photo`}
                title={photo.credit ? `Photo by ${photo.credit}${photo.license ? ` (${photo.license})` : ""}` : undefined}
                className="mx-auto mb-2 w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover border border-green-100 shadow-sm"
              />
            )}
            <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-gray-900 leading-tight mb-2">
              {heading}
            </h2>
            <p className="text-[13.5px] text-gray-600 max-w-md mx-auto leading-snug">
              {subheading}
            </p>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5 max-w-xl mx-auto">
            <li className="flex items-start gap-2 rounded-lg bg-green-50/60 border border-green-100 px-3 py-2">
              <Bell className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" strokeWidth={2.25} />
              <span className="text-[12.5px] text-gray-800 leading-snug">
                Monthly reminders timed to your ZIP
              </span>
            </li>
            <li className="flex items-start gap-2 rounded-lg bg-green-50/60 border border-green-100 px-3 py-2">
              <Save className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" strokeWidth={2.25} />
              <span className="text-[12.5px] text-gray-800 leading-snug">
                Save and track this lawn
              </span>
            </li>
            <li className="flex items-start gap-2 rounded-lg bg-green-50/60 border border-green-100 px-3 py-2">
              <CalendarCheck className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" strokeWidth={2.25} />
              <span className="text-[12.5px] text-gray-800 leading-snug">
                Sync tasks to your calendar
              </span>
            </li>
          </ul>
        </>
      )}

      <div className="max-w-md mx-auto space-y-3">
        {(() => {
          const emailField = (
            <div>
              <Input
                id="result-upgrade-email"
                ref={emailInputRef}
                type="email"
                name="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={onEmailChange}
                onKeyDown={onEmailKeyPress}
                onFocus={handleEmailFocus}
                placeholder={copy?.emailPlaceholder ?? "Enter your email"}
                aria-label={copy?.emailAriaLabel ?? "Email address"}
                aria-invalid={emailError ? true : undefined}
                className="w-full border-2 border-green-200 rounded-xl focus:border-green-500 focus:ring-0 focus:outline-hidden transition-colors duration-200 px-4 py-3 md:py-4 text-base md:text-lg"
                autoComplete="email"
                style={{ boxShadow: 'none' }}
              />

              {emailError && (
                <p role="alert" className="mt-2 flex items-center gap-1.5 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {emailError}
                </p>
              )}

              {emailSuggestion && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-800">
                      Did you mean <strong>{emailSuggestion.correctedDomain}</strong>?
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onAcceptSuggestion}
                      className="text-sm font-medium text-green-700 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50 transition-colors"
                    >
                      Yes, fix it
                    </button>
                    <button
                      type="button"
                      onClick={onDismissSuggestion}
                      className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          )

          const sendButton = (
            <Button
              onClick={() => {
                onPrimaryCtaClick?.("email")
                onSubmit()
              }}
              disabled={isSubmitting}
              // whitespace-normal isn't in the precompiled Tailwind bundle; set it
              // inline so the label wraps (never clips) regardless of the scanner.
              style={{ whiteSpace: "normal" }}
              // text-base at every width: the md:text-lg bump bought ~3 fewer
              // characters per line and was the other half of why named-problem
              // labels wrapped in the result column.
              className="w-full text-center leading-snug bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 md:py-4 px-6 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {sendLabel}
                </>
              )}
            </Button>
          )

          const oauthSection = timelineRedirectUrl ? (
            <>
              {contextLine && (
                <p className="text-[13px] font-medium text-green-800 text-center leading-snug">
                  {contextLine}
                </p>
              )}
              {savePlanOauth ? (
                <SocialLoginButtons
                  placement={oauthPlacement}
                  redirectTo={timelineRedirectUrl}
                  savedPlanContext={savedPlanContext}
                  pill
                  appleSolid
                  googleLabel={copy?.googleLabel ?? "Continue with Google"}
                  appleLabel={copy?.appleLabel ?? "Continue with Apple"}
                  onBeforeRedirect={handleOAuthBeforeRedirect}
                />
              ) : (
                <SocialLoginButtons
                  placement={oauthPlacement}
                  redirectTo={timelineRedirectUrl}
                  savedPlanContext={savedPlanContext}
                  compact
                  onBeforeRedirect={handleOAuthBeforeRedirect}
                />
              )}
            </>
          ) : null

          // Digest (embedded): both paths always visible, no reveal/collapse
          // states (those belonged to the standalone card's retired
          // experiments). Order follows askOrder — email_first leads with the
          // email field (matching the identify digest), otherwise OAuth leads.
          if (embedded) {
            if (emailFirst) {
              return (
                <>
                  {emailField}
                  {sendButton}
                  {oauthSection && (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                          or save it to a free account
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      {oauthSection}
                    </>
                  )}
                </>
              )
            }
            // 1a treatment: OAuth is THE action; email hides behind a quiet
            // reveal link until tapped (or when the field already has content
            // or a pending typo suggestion, which must stay visible). If OAuth
            // can't render (no redirect URL), email is the only path — keep it
            // expanded rather than leaving the card with no visible CTA.
            // emailRevealed lets the owning surface open the lane from its own
            // CTA (or keep it open permanently), falling through to the
            // divider + field layout below.
            if (
              savePlanOauth &&
              oauthSection &&
              !emailRevealed &&
              !emailExpanded &&
              !email &&
              !emailSuggestion
            ) {
              return (
                <>
                  {oauthSection}
                  <p className="text-center text-[13px] text-gray-500">
                    or{" "}
                    <button
                      type="button"
                      onClick={handleRevealEmail}
                      className="font-semibold text-green-700 underline underline-offset-2 hover:text-green-800 transition-colors"
                    >
                      {copy?.emailRevealLabel ?? "get it by email"}
                    </button>
                  </p>
                </>
              )
            }
            return (
              <>
                {oauthSection}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                    {copy?.emailDividerLabel ?? "or email me the plan"}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                {emailField}
                {sendButton}
              </>
            )
          }

          // email_first: single-field email is THE primary ask; OAuth account
          // creation is demoted to a quiet secondary reveal below it.
          if (emailFirst) {
            return (
              <>
                {emailField}
                {sendButton}
                {oauthSection &&
                  (oauthRevealed ? (
                    <div className="space-y-3 pt-1">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                          or save it to a free account
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      {oauthSection}
                    </div>
                  ) : (
                    <p className="text-center">
                      <button
                        type="button"
                        onClick={() => setOauthRevealed(true)}
                        className="text-[13px] text-gray-500 underline underline-offset-2 hover:text-gray-700 transition-colors"
                      >
                        or save it to a free account
                      </button>
                    </p>
                  ))}
              </>
            )
          }

          // account_first (default): OAuth primary, email below the divider.
          return (
            <>
              {oauthSection}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                  {copy?.emailDividerLabel ?? "or email me the plan"}
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              {emailField}
              {sendButton}
            </>
          )
        })()}
      </div>
    </div>
  )
}
